import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { IconPrinter, IconRefreshCw, IconSearch } from '../../components/layout/icons';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/Feedback';
import { ExcelExportButton } from '../../components/ui/ExcelExportButton';
import { SortableTh } from '../../components/ui/SortableTh';
import { ApiError } from '../../lib/api-client';
import { formatDate } from '../../lib/datetime';
import { exportRowsToExcel } from '../../lib/excel-export';
import { useTableSort } from '../../lib/table-sort';
import { todayIso } from './report-filters';
import { useHospitalStayReport } from './reports.queries';
import type { HospitalStayFilters, HospitalStayRow, HospitalStaySection } from './types';

const initialFilters = (): HospitalStayFilters => ({ date: todayIso(), department: '' });

const sectionMeta: Record<HospitalStaySection, { label: string; accent: string }> = {
  opd: { label: 'OPD', accent: 'cyan' },
  emg: { label: 'EMG', accent: 'blue' },
  ipd: { label: 'IPD', accent: 'green' },
  daycare: { label: 'Daycare', accent: 'amber' },
  dialysis: { label: 'Dialysis', accent: 'red' },
  ot: { label: 'OT', accent: 'slate' },
};

const sections = Object.keys(sectionMeta) as HospitalStaySection[];
const dateOnly = (value: string | null) => value ? formatDate(value).replaceAll('/', '-') : '-';
const patientInfo = (row: HospitalStayRow) => `${row.gender ?? '-'} || ${row.ageDisplay}`;
const patientText = (row: HospitalStayRow) => `${row.patientName}${row.uhid ? ` (${row.uhid})` : ''}`;
const wardBedText = (row: HospitalStayRow) => [row.ward, row.bed].filter((value) => value && value !== '-').join(' || ') || '-';

const detailPath = (row: HospitalStayRow) => {
  if (row.section === 'opd') return `/opd/${row.id}`;
  if (row.section === 'emg') return `/emg/${row.id}`;
  if (row.section === 'dialysis') return `/dialysis/${row.id}`;
  if (row.section === 'ot') return `/ot/${row.id}`;
  return `/ipd-daycare/${row.id}`;
};

export const HospitalStayReportPage = () => {
  const [draft, setDraft] = useState<HospitalStayFilters>(initialFilters);
  const [filters, setFilters] = useState<HospitalStayFilters>(initialFilters);
  const [activeSection, setActiveSection] = useState<HospitalStaySection>('ipd');
  const query = useHospitalStayReport(filters);
  const activeRows = useMemo(
    () => (query.data?.rows ?? []).filter((row) => row.section === activeSection),
    [activeSection, query.data?.rows],
  );
  const tableSort = useTableSort(activeRows, {
    number: (row) => row.number,
    patient: (row) => row.patientName,
    patientInfo,
    doctor: (row) => row.doctorName,
    department: (row) => row.department,
    wardBed: wardBedText,
    admissionDate: (row) => row.admissionDate,
    dischargeDate: (row) => row.dischargeDate,
  });
  const showStayColumns = activeSection !== 'opd';
  const busy = query.isFetching;
  const errorMessage = query.error instanceof ApiError ? query.error.message : 'Hospital stay records could not be loaded.';

  const applyFilters = (event: FormEvent) => {
    event.preventDefault();
    setFilters(draft);
  };

  const reset = () => {
    const next = initialFilters();
    setDraft(next);
    setFilters(next);
  };

  const exportToExcel = async () => {
    const section = sectionMeta[activeSection].label;
    await exportRowsToExcel({
      fileName: `Hospital-Stay-Report-${section}-${filters.date}`,
      title: `Hospital Stay Report - ${section}`,
      subtitle: dateOnly(filters.date),
      columns: [
        { header: '#', width: 6, align: 'right', map: (_row, index) => index + 1 },
        { header: `${section} ID`, width: 14, map: (row) => row.number },
        { header: 'Patient', width: 26, map: patientText },
        { header: 'Patient Info', width: 20, map: patientInfo },
        { header: 'Doctor', width: 24, map: (row) => row.doctorName },
        { header: 'Department', width: 22, map: (row) => row.department },
        ...(showStayColumns ? [{ header: 'Ward & Bed', width: 22, map: wardBedText }] : []),
        { header: 'Admission Date', width: 16, map: (row) => dateOnly(row.admissionDate) },
        ...(showStayColumns ? [{ header: 'Discharge', width: 16, map: (row: HospitalStayRow) => dateOnly(row.dischargeDate) }] : []),
      ],
      rows: tableSort.rows,
    });
  };

  return (
    <div className="page-stack hospital-stay-page">
      <section className="content-card hospital-stay-report-card">
        <header className="hospital-stay-titlebar"><h1>Hospital Stay REPORT</h1></header>
        <form className="hospital-stay-filter-card report-print-hidden" onSubmit={applyFilters}>
          <label>
            <span className="sr-only">Report date</span>
            <input
              aria-label="Report date"
              type="date"
              required
              value={draft.date}
              max={todayIso()}
              onChange={(event) => setDraft((current) => ({ ...current, date: event.target.value }))}
            />
          </label>
          <label>
            <span className="sr-only">Department</span>
            <select
              aria-label="Department"
              value={draft.department}
              onChange={(event) => setDraft((current) => ({ ...current, department: event.target.value }))}
            >
              <option value="">Select Department</option>
              {(query.data?.departments ?? []).map((department) => <option key={department.id} value={department.id}>{department.name}</option>)}
            </select>
          </label>
          <button className="report-toolbar-btn search" type="submit" disabled={busy}><IconSearch />Search</button>
          <button className="report-toolbar-btn reset" type="button" onClick={reset} disabled={busy}><IconRefreshCw />Reset</button>
          <button className="report-toolbar-btn print" type="button" onClick={() => window.print()}><IconPrinter />Print</button>
          <ExcelExportButton className="hospital-stay-excel-action" onExport={exportToExcel} disabled={busy || activeRows.length === 0} label="Excel" />
        </form>
      </section>

      <div className="hospital-stay-summary-grid">
        {sections.map((section) => {
          const meta = sectionMeta[section];
          return (
            <button
              key={section}
              type="button"
              className={`hospital-stay-summary-card ${meta.accent}${activeSection === section ? ' active' : ''}`}
              onClick={() => setActiveSection(section)}
            >
              <span>{meta.label}</span>
              <strong>{(query.data?.counts[section] ?? 0).toLocaleString('en-IN')}</strong>
              <small>Click to view list</small>
            </button>
          );
        })}
      </div>

      <section className="content-card hospital-stay-card">
        <header className="hospital-stay-card-header">
          <h2>{sectionMeta[activeSection].label} Patient List</h2>
        </header>

        {query.isLoading ? <LoadingState /> : null}
        {query.isError ? <ErrorState title="Unable to load hospital stay report" message={errorMessage} action={{ label: 'Try again', onClick: () => void query.refetch() }} /> : null}
        {query.isSuccess && activeRows.length === 0 ? <EmptyState title="No patient stays found" message="Change the report date, department, or selected section." /> : null}
        {query.isSuccess && activeRows.length > 0 ? (
          <div className="hospital-stay-table-scroll">
            <table className="hospital-stay-table">
              <thead>
                <tr>
                  <th>#</th>
                  <SortableTh control={tableSort} column="number">{sectionMeta[activeSection].label} ID</SortableTh>
                  <SortableTh control={tableSort} column="patient">Patient</SortableTh>
                  <SortableTh control={tableSort} column="patientInfo">Patient Info</SortableTh>
                  <SortableTh control={tableSort} column="doctor">Doctor</SortableTh>
                  <SortableTh control={tableSort} column="department">Department</SortableTh>
                  {showStayColumns ? <SortableTh control={tableSort} column="wardBed">Ward &amp; Bed</SortableTh> : null}
                  <SortableTh control={tableSort} column="admissionDate">Admission Date</SortableTh>
                  {showStayColumns ? <SortableTh control={tableSort} column="dischargeDate">Discharge</SortableTh> : null}
                </tr>
              </thead>
              <tbody>
                {tableSort.rows.map((row, index) => (
                  <tr key={`${row.section}-${row.id}`}>
                    <td>{index + 1}</td>
                    <td><Link className="hospital-stay-id" to={detailPath(row)}>{row.number}</Link></td>
                    <td>{patientText(row)}</td>
                    <td>{patientInfo(row)}</td>
                    <td>{row.doctorName}</td>
                    <td>{row.department}</td>
                    {showStayColumns ? <td>{wardBedText(row)}</td> : null}
                    <td>{dateOnly(row.admissionDate)}</td>
                    {showStayColumns ? <td>{dateOnly(row.dischargeDate)}</td> : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </div>
  );
};
